'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavItemIcon } from '@/components/layout/nav-item-icon';
import {
  mobileDropdownChildClass,
  mobileDropdownChildListClass,
  mobileDropdownParentClass,
  mobileNavIconClass,
  MOBILE_DROPDOWN_CHILD_ROW,
  MOBILE_NAV_ICON_SLOT,
  type MobileSidePanelTheme,
} from '@/components/layout/mobile-app-side-panel-theme';
import { SheetClose } from '@/components/ui/sheet';
import {
  ACCOUNT_SETTINGS_TABS,
  ORGANIZATION_SETTINGS_TABS,
  isSettingsNavTabActive,
  type SettingsNavTab,
} from '@/lib/navigation/settings-nav-config';
import { useStableOrgAdmin } from '@/hooks/use-stable-org-admin';
import { useRTLUtils } from '@/lib/translation';
import { cn } from '@/lib/utils';

export const MOBILE_ACCOUNT_SETTINGS_GROUP_KEY = '__account_settings__';

type MobileAccountSettingsNavProps = {
  theme: MobileSidePanelTheme;
  onNavigate?: () => void;
  isOpen: boolean;
  onToggle: () => void;
};

function filterTabs(tabs: SettingsNavTab[], isOrgAdmin: boolean) {
  return tabs.filter((tab) => !tab.orgAdminOnly || isOrgAdmin);
}

function SettingsLinkGroup({
  title,
  links,
  pathname,
  theme,
  onNavigate,
  textAlignClass,
}: {
  title: string;
  links: SettingsNavTab[];
  pathname: string;
  theme: MobileSidePanelTheme;
  onNavigate?: () => void;
  textAlignClass: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-0.5">
      <p
        className={cn(
          'px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wide opacity-70',
          textAlignClass,
        )}
      >
        {title}
      </p>
      {links.map((link) => {
        const active = isSettingsNavTabActive(pathname, link.href);
        return (
          <SheetClose asChild key={link.href}>
            <Link
              href={link.href}
              onClick={onNavigate}
              className={cn(
                MOBILE_DROPDOWN_CHILD_ROW,
                mobileDropdownChildClass(theme, active),
                textAlignClass,
              )}
            >
              {t(link.labelKey)}
            </Link>
          </SheetClose>
        );
      })}
    </div>
  );
}

export function MobileAccountSettingsNav({
  theme,
  onNavigate,
  isOpen,
  onToggle,
}: MobileAccountSettingsNavProps) {
  const pathname = usePathname() ?? '';
  const { t } = useTranslation();
  const { textAlignClass, isRTL } = useRTLUtils();
  const { stableIsOrgAdmin, orgRoleNavReady, isLoaded } = useStableOrgAdmin();

  const groupActive = pathname.startsWith('/settings');
  const isOrgAdmin = isLoaded && orgRoleNavReady ? stableIsOrgAdmin : false;

  const accountLinks = filterTabs(ACCOUNT_SETTINGS_TABS, isOrgAdmin);
  const organizationLinks = filterTabs(ORGANIZATION_SETTINGS_TABS, isOrgAdmin);

  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        onClick={onToggle}
        className={mobileDropdownParentClass(theme, groupActive)}
        aria-expanded={isOpen}
        aria-label={t('account.settings')}
      >
        <span className={MOBILE_NAV_ICON_SLOT}>
          <NavItemIcon icon="settings" className={mobileNavIconClass(theme, groupActive)} />
        </span>
        <span className={cn('min-w-0 flex-1 truncate', textAlignClass)}>{t('account.settings')}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 opacity-70 transition-transform', isOpen && 'rotate-180')}
        />
      </button>
      {isOpen ? (
        <div className={mobileDropdownChildListClass(theme, isRTL)}>
          <SettingsLinkGroup
            title={t('settings.menu.account')}
            links={accountLinks}
            pathname={pathname}
            theme={theme}
            onNavigate={onNavigate}
            textAlignClass={textAlignClass}
          />
          <SettingsLinkGroup
            title={t('settings.menu.organization')}
            links={organizationLinks}
            pathname={pathname}
            theme={theme}
            onNavigate={onNavigate}
            textAlignClass={textAlignClass}
          />
        </div>
      ) : null}
    </div>
  );
}
